import { collection, deleteDoc, doc, getDoc, getDocs } from "firebase/firestore";
import React, { useCallback, useEffect, useState } from "react";
import { db } from "../../firebaseConfig";

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  instagram: string;
  friendEmail: string;
  invitationCode?: string; // Add the invitation code property
  invitedBy?: string;
}

interface UserListProps {
  eventId: string;
}

const UserList: React.FC<UserListProps> = ({ eventId }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosedEvent, setIsClosedEvent] = useState(false);
  const [codesInfo, setCodesInfo] = useState({
    totalCodes: 0,
    availableCodes: 0,
    usedCodes: 0
  });

  const fetchEventDetails = useCallback(async () => {
    if (!eventId) return;
    
    try {
      const eventDoc = await getDoc(doc(db, "events", eventId));
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        setIsClosedEvent(eventData.isClosedEvent || false);
        
        // If it's a closed event, fetch invitation codes information
        if (eventData.isClosedEvent) {
          const codesDoc = await getDoc(doc(db, `events/${eventId}/metadata/invitationCodes`));
          if (codesDoc.exists()) {
            const codesData = codesDoc.data();
            setCodesInfo({
              totalCodes: codesData.totalCodes || 0,
              availableCodes: (codesData.availableCodes || []).length,
              usedCodes: (codesData.totalCodes || 0) - (codesData.availableCodes || []).length
            });
          }
        }
      }
    } catch (err) {
      console.error("Error fetching event details:", err);
    }
  }, [eventId]);

  const fetchUsers = useCallback(async () => {
    if (!eventId) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(
        collection(db, `events/${eventId}/registrations`)
      );
      const fetchedUsers: User[] = querySnapshot.docs.map((doc) => ({
        ...(doc.data() as Omit<User, "id">),
        id: doc.id,
      }));

      // Process invitations
      const usersWithInvitations = fetchedUsers.map((user) => {
        const invitedBy = fetchedUsers.find(
          (u) => u.friendEmail === user.email
        );
        return {
          ...user,
          invitedBy: invitedBy ? invitedBy.name : user.invitedBy,
        };
      });

      setUsers(usersWithInvitations);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventDetails();
    fetchUsers();
  }, [fetchEventDetails, fetchUsers]);

  const deleteUser = async (userId: string) => {
    if (!eventId || !userId) return;

    if (window.confirm("Are you sure you want to remove this user?")) {
      try {
        await deleteDoc(doc(db, `events/${eventId}/registrations`, userId));
        alert("User removed successfully");
        fetchUsers();
      } catch (error) {
        console.error("Error removing user: ", error);
        alert("An error occurred while removing the user. Please try again.");
      }
    }
  };

  const downloadCSV = () => {
    if (users.length === 0) return;

    // Determine CSV columns based on event type
    let headers = [
      "Name",
      "Email",
      "Phone Number",
      "Instagram",
      "Invited Friend",
      "Invited By"
    ];
    
    // Add invitation code column for closed events
    if (isClosedEvent) {
      headers.push("Invitation Code");
    }

    // Map user data to rows
    const userRows = users.map((user) => {
      const row = [
        user.name,
        user.email,
        user.phoneNumber,
        user.instagram,
        user.friendEmail,
        user.invitedBy || ""
      ];
      
      // Add invitation code for closed events
      if (isClosedEvent) {
        row.push(user.invitationCode || "");
      }
      
      return row;
    });

    const allRows = [
      headers,
      ...userRows
    ];

    const csvContent = allRows
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${eventId}_registrations.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-accent text-white p-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold">Registered Users</h2>
        {users.length > 0 && (
          <button
            onClick={downloadCSV}
            className="bg-white text-accent px-4 py-2 rounded-md hover:bg-gray-100 transition-colors"
          >
            Download CSV
          </button>
        )}
      </div>
      
      {/* Invitation codes summary for closed events */}
      {isClosedEvent && (
        <div className="bg-gray-50 p-4 border-b">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Invitation Codes Summary</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white shadow-sm rounded-md p-4 text-center">
              <div className="text-3xl font-bold text-primary">{codesInfo.totalCodes}</div>
              <div className="text-sm text-gray-500">Total Codes</div>
            </div>
            <div className="bg-white shadow-sm rounded-md p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{codesInfo.usedCodes}</div>
              <div className="text-sm text-gray-500">Used Codes</div>
            </div>
            <div className="bg-white shadow-sm rounded-md p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{codesInfo.availableCodes}</div>
              <div className="text-sm text-gray-500">Available Codes</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <p className="text-center text-red-600">{error}</p>
        ) : users.length === 0 ? (
          <p className="text-center text-gray-600">No users registered yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Phone
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Instagram
                  </th>
                  {isClosedEvent && (
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Invitation Code
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Invited Friend
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Invited By
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr
                    key={user.id}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.phoneNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.instagram}
                    </td>
                    {isClosedEvent && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {user.invitationCode || "-"}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.friendEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.invitedBy || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;